import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { FONT_FAMILIES } from '../../assets/fonts';
import { uploadFile } from '../../utils/storageService';
import { supabase } from '../../utils/supabaseClient';
import './BlogEditor.css';

// Same hand-rolled approach as TextEditor.js's FontFamily extension — no
// @tiptap/extension-font-family package is installed, and this app already
// has a proven pattern for it.
const FontFamily = Extension.create({
  name: 'fontFamily',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: (element) => element.style.fontFamily,
            renderHTML: (attributes) => {
              if (!attributes.fontFamily) return {};
              return { style: `font-family: ${attributes.fontFamily}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontFamily: (fontFamily) => ({ chain }) => chain().setMark('textStyle', { fontFamily }).run(),
    };
  },
});

const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize: (fontSize) => ({ chain }) => chain().setMark('textStyle', { fontSize }).run(),
    };
  },
});

const FONT_SIZES = ['14px', '16px', '18px', '20px', '24px', '28px', '32px', '40px'];

export default function BlogEditor({ content, onChange, postId }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Bold,
      Italic,
      Underline,
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false }),
      Image,
    ],
    content: content || '',
    editorProps: {
      attributes: { class: 'blog-editor-prosemirror' },
    },
    onUpdate: ({ editor: ed }) => {
      if (onChange) onChange(ed.getHTML());
    },
  });

  // Keep the editor in sync if the parent swaps `content` out from under it
  // (e.g. loading an existing post after the editor already mounted empty).
  const lastLoadedRef = useRef(content);
  useEffect(() => {
    if (!editor) return;
    if (content !== lastLoadedRef.current && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
      lastLoadedRef.current = content;
    }
  }, [content, editor]);

  const insertImageFile = async (file) => {
    if (!file || !editor) return;
    setUploading(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        alert('Войдите в аккаунт, чтобы загрузить изображение.');
        return;
      }
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const path = `teachers/${authUser.id}/blog/${postId || 'new'}/image-${Date.now()}.${ext}`;
      const url = await uploadFile(file, path);
      editor.chain().focus().setImage({ src: url }).run();
    } catch (e) {
      console.error('Error uploading blog image:', e);
      alert('Не удалось загрузить изображение. Попробуйте ещё раз.');
    } finally {
      setUploading(false);
    }
  };

  const handlePickImage = () => fileInputRef.current?.click();
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) insertImageFile(file);
  };

  if (!editor) return null;

  return (
    <div className="blog-editor">
      <div className="blog-editor-toolbar">
        <button
          type="button"
          className={editor.isActive('bold') ? 'is-active' : ''}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          className={editor.isActive('italic') ? 'is-active' : ''}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          className={editor.isActive('underline') ? 'is-active' : ''}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline"
        >
          <u>U</u>
        </button>

        <span className="blog-editor-divider" />

        <select
          className="blog-editor-select"
          onChange={(e) => {
            const val = e.target.value;
            if (val) editor.chain().focus().setFontFamily(val).run();
          }}
          value={editor.getAttributes('textStyle').fontFamily || ''}
          title="Font family"
        >
          <option value="">Шрифт по умолчанию</option>
          {FONT_FAMILIES.map((f) => (
            <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
          ))}
        </select>

        <select
          className="blog-editor-select blog-editor-select-narrow"
          onChange={(e) => {
            const val = e.target.value;
            if (val) editor.chain().focus().setFontSize(val).run();
          }}
          value={editor.getAttributes('textStyle').fontSize || ''}
          title="Font size"
        >
          <option value="">Размер</option>
          {FONT_SIZES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <input
          type="color"
          className="blog-editor-color"
          value={editor.getAttributes('textStyle').color || '#1a1a2e'}
          onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          title="Text color"
        />

        <span className="blog-editor-divider" />

        <button
          type="button"
          className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          title="Align left"
        >⟸</button>
        <button
          type="button"
          className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          title="Align center"
        >⟺</button>
        <button
          type="button"
          className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          title="Align right"
        >⟹</button>

        <span className="blog-editor-divider" />

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          title="Bullet list"
        >•—</button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
          title="Heading"
        >H2</button>

        <span className="blog-editor-divider" />

        <button type="button" onClick={handlePickImage} disabled={uploading} title="Insert image">
          {uploading ? '…' : '🖼 Картинка'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>
      <EditorContent editor={editor} className="blog-editor-content" />
    </div>
  );
}
